import { Component, Input, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { ProjectFilters } from '../../models/projects/project-filters.model';
import { AllCommunitiesFilterCheckboxComponent } from '../shared/all-communities-filter-checkbox/all-communities-filter-checkbox.component';
import { BaseFiltersComponent } from '../shared/base-filters/base-filters.component';
import { CommunitiesFiltersDropdownComponent } from '../shared/communities-filters-dropdown/communities-filters-dropdown.component';
import { OrganizationFiltersCheckboxComponent } from '../shared/organization-filters-checkbox/organization-filters-checkbox.component';
import { InterestsFiltersCheckboxComponent } from './components/interests-filters-checkbox/interests-filters-checkbox.component';
import { OrganizationSkillsFiltersCheckboxComponent } from './components/organization-skills-filters-checkbox/organization-skills-filters-checkbox.component';
import { ProjectParticipateFiltersDropdownComponent } from './components/project-participate-filters-dropdown/project-participate-filters-dropdown.component';
import { ProjectTypesFiltersDropdownComponent } from './components/project-types-filters-dropdown/project-types-filters-dropdown.component';
import { SkillsFiltersCheckboxComponent } from './components/skills-filters-checkbox/skills-filters-checkbox.component';

@Component({
    selector: 'app-project-filters',
    templateUrl: './project-filters.component.html',
})
export class ProjectFiltersComponent extends BaseFiltersComponent<ProjectFilters> {
    @ViewChild('allCommunitiesFilter', { static: false })
    private readonly allCommunitiesFilter: AllCommunitiesFilterCheckboxComponent;
    @ViewChild('communitiesFilters', { static: false })
    private readonly communitiesFilters: CommunitiesFiltersDropdownComponent;
    @ViewChild('interestsFilter', { static: false })
    private readonly myInterestsFilter: InterestsFiltersCheckboxComponent;
    @ViewChild('organizationSkillsFilter', { static: false })
    private readonly myOrganizationSkillsFilter: OrganizationSkillsFiltersCheckboxComponent;
    @ViewChild('skillsFilter', { static: false })
    private readonly mySkillsFilter: SkillsFiltersCheckboxComponent;
    @ViewChild('organizationFilter', { static: false })
    private readonly organizationFilter: OrganizationFiltersCheckboxComponent;
    @ViewChild('projectTypesFilters', { static: false })
    private readonly projectTypesFilters: ProjectTypesFiltersDropdownComponent;
    @ViewChild('projectInvolvedUserFilterItems', { static: false })
    private readonly projectInvolvedUserFilterItems: ProjectParticipateFiltersDropdownComponent;
    public areCommunitiesFiltersReady = false;
    public areProjectTypesFilterReady = false;
    @Input() public canSeeAllCommunitiesFilter: boolean;
    @Input() public canSeeOrganizationSkillsFilter: boolean;
    public dictionary = environment.dictionary;
    public isAllCommunitiesFilterReady = false;
    public isMyInterestsFilterReady = false;
    public isMyOrganizationSkillsFilterReady = false;
    public isMySkillsFilterReady = false;
    public isOrganizationFilterReady = false;
    public isUserInvolvedProjectsFilterReady = false;
    public uncheckAllCommunities = false;
    public visibleByAllButtonName = this.translate.instant('shared.projectScopeSelection.visible');

    constructor(private translate: TranslateService) {
        super();
    }

    public checkFiltersReadiness() {
        if (
            this.isOrganizationFilterReady &&
            this.areCommunitiesFiltersReady &&
            (this.isAllCommunitiesFilterReady || !this.canSeeAllCommunitiesFilter) &&
            this.isMyInterestsFilterReady &&
            this.isMySkillsFilterReady &&
            (!this.canSeeOrganizationSkillsFilter || (this.canSeeOrganizationSkillsFilter && this.isMyOrganizationSkillsFilterReady)) &&
            this.areProjectTypesFilterReady &&
            this.isUserInvolvedProjectsFilterReady
        ) {
            this.ready.emit();
        }
    }

    public override extractSelectedFilters(): ProjectFilters {
        const filters = new ProjectFilters();
        const selectedCommunities = this.getSelectedCommunities();

        filters.isMyOrganizationChecked = this.organizationFilter.organizationFilterItem.checked;
        filters.isMySkillsChecked = this.mySkillsFilter.skillsFilterItem.checked;

        filters.isMyOrganizationSkillsChecked = false;
        if (this.canSeeOrganizationSkillsFilter) {
            filters.isMyOrganizationSkillsChecked = this.myOrganizationSkillsFilter.organizationSkillsFilterItem.checked;
        }
        filters.communitiesIds = this.getCommunitiesIds(selectedCommunities);
        if (this.canSeeAllCommunitiesFilter) {
            if (selectedCommunities.length > 0) {
                filters.communitiesIds = selectedCommunities;
                filters.isAllCommunitiesChecked = this.allCommunitiesFilter.allCommunitiesFilterItem.checked;
            } else {
                filters.communitiesIds = this.allCommunitiesFilter.allCommunitiesFilterItem.checked ? [] : filters.communitiesIds;

                filters.isAllCommunitiesChecked = true;
            }
        }
        filters.isMyInterestsChecked = this.myInterestsFilter.interestsFilterItem.checked;

        filters.projectTypesIds = this.projectTypesFilters.projectTypesFilterItems.filter((item) => item.checked).map((item) => item.key);

        filters.projectParticipation = this.projectInvolvedUserFilterItems.getProjectParticipation();

        return filters;
    }
    private getSelectedCommunities(): string[] {
        return this.communitiesFilters.userCommunitiesFilterItems.filter((item) => item.checked).map((item) => item.key);
    }
    private getCommunitiesIds(selectedCommunities: string[]): string[] {
        if (selectedCommunities.length > 0) {
            return selectedCommunities;
        }

        if (this.organizationFilter.organizationFilterItem.checked) {
            return [];
        }

        return this.communitiesFilters.userCommunitiesFilterItems.map((item) => item.key);
    }
    public onAllCommunitiesFilterReady() {
        this.isAllCommunitiesFilterReady = true;
        this.checkFiltersReadiness();
    }

    public onAllCommunitiesFilterSelectionChanged() {
        this.selectionChanged.emit();
    }

    public onCommunitiesFiltersReady() {
        this.areCommunitiesFiltersReady = true;
        this.loadAllCommunitiesFilter();
        this.checkFiltersReadiness();
    }

    public onCommunitiesFiltersSelectionChanged() {
        let noCommunitySelected = true;
        this.communitiesFilters.userCommunitiesFilterItems.forEach((community) => {
            if (community.checked) noCommunitySelected = false;
        });

        if (noCommunitySelected) this.communitiesFilters.resetFilters();

        this.selectionChanged.emit();
    }

    public onFiltersSelectionChanged() {
        this.selectionChanged.emit();
    }

    public onInterestsFilterReady() {
        this.isMyInterestsFilterReady = true;
        this.checkFiltersReadiness();
    }

    public onOrganizationFilterReady() {
        this.isOrganizationFilterReady = true;
        this.checkFiltersReadiness();
    }

    public onOrganizationFiltersSelectionChanged() {
        if (!this.organizationFilter.organizationFilterItem.checked && this.uncheckAllCommunities) {
            this.uncheckAllCommunities = false;
        }

        this.selectionChanged.emit();
    }

    public onParticipationFiltersSelectionChanged() {
        this.selectionChanged.emit();
    }

    public getProjectParticipation() {
        return this.projectInvolvedUserFilterItems.getProjectParticipation();
    }

    public onOrganizationSkillsFilterReady() {
        this.isMyOrganizationSkillsFilterReady = true;
        this.checkFiltersReadiness();
    }

    public onProjectTypesFiltersReady() {
        this.areProjectTypesFilterReady = true;
        this.checkFiltersReadiness();
    }

    public onSkillsFilterReady() {
        this.isMySkillsFilterReady = true;
        this.checkFiltersReadiness();
    }
    public onUserInvolvedProjectsFilterReady() {
        this.isUserInvolvedProjectsFilterReady = true;
        this.checkFiltersReadiness();
    }
    public resetFilters(): void {
        this.uncheckAllCommunities = false;
        this.organizationFilter.resetFilters();
        this.communitiesFilters.resetFilters();
        this.myInterestsFilter.resetFilters();
        this.mySkillsFilter.resetFilters();
        if (this.canSeeOrganizationSkillsFilter) {
            this.myOrganizationSkillsFilter.resetFilters();
        }

        this.projectTypesFilters.resetFilters();
        this.projectInvolvedUserFilterItems.resetFilters();
        this.loadAllCommunitiesFilter();
        this.selectionChanged.emit();
    }

    public override setSelectedFilters(filters: ProjectFilters) {
        this.organizationFilter.organizationFilterItem.checked = filters.isMyOrganizationChecked;
        this.mySkillsFilter.skillsFilterItem.checked = filters.isMySkillsChecked;

        if (this.canSeeOrganizationSkillsFilter) {
            this.myOrganizationSkillsFilter.organizationSkillsFilterItem.checked = filters.isMyOrganizationSkillsChecked;
        }

        this.myInterestsFilter.interestsFilterItem.checked = filters.isMyInterestsChecked;

        this.projectTypesFilters.setSelectedFilters(filters.projectTypesIds);
    }
    private loadAllCommunitiesFilter() {
        this.communitiesFilters.userCommunitiesFilterItems.forEach((item) => {
            item.check();
            if (item.groups) {
                item.groups.forEach((group) => group.check());
            }
        });
    }
}
